import { useRef, useState } from "react";
import { blobUrl, uploadAsset } from "../api.js";
import type { ConfigPanelProps } from "./configTypes.js";

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** The active `icon:` value in the appearance file (ignores the `# icon:` hint). */
function readIcon(text: string): string {
  for (const line of text.split("\n")) {
    const m = /^\s*icon\s*:\s*(.*)$/.exec(line);
    if (m) return m[1].trim();
  }
  return "";
}

/** Set (or clear) the appearance file's `icon:` key, editing the box text. Fills
 *  the commented `# icon:` hint in place if that's all there is; else appends. */
function writeIcon(text: string, value: string): string {
  const lines = text.split("\n");
  const active = lines.findIndex((l) => /^\s*icon\s*:/.test(l));
  if (active >= 0) {
    if (value) lines[active] = `icon: ${value}`;
    else lines.splice(active, 1);
    return lines.join("\n");
  }
  if (!value) return text;
  const hint = lines.findIndex((l) => /^\s*#\s*icon\s*:/.test(l));
  if (hint >= 0) {
    lines[hint] = `icon: ${value}`;
    return lines.join("\n");
  }
  return (text.endsWith("\n") ? text : text + "\n") + `icon: ${value}\n`;
}

/**
 * Favicon uploader — the "extra UI" for the `appearance` config file. Uploads an
 * image into `datawiki-config` (its draft worktree) and writes the resulting path
 * into the `icon:` key of the box above. The raw box still owns the bytes; Save +
 * publish is what applies it (a publish reloads config and the favicon updates).
 */
export function AppearancePanel({ text, setText, repo, editable }: ConfigPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Cache-bust the preview: the default at /favicon.svg is aggressively cached
  // by the browser, so a bare src can show a stale (previously-configured) icon.
  // Bumped on every upload/remove so the swatch always reflects the current pick.
  const [bust, setBust] = useState(() => Date.now());
  const icon = readIcon(text);
  const previewSrc = `${icon ? blobUrl(repo, icon) : "/favicon.svg"}?cb=${bust}`;

  const onPick = async (file: File): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const { path } = await uploadAsset(repo, file, "appearance");
      setText(writeIcon(text, path));
      setBust(Date.now());
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dw-settings-check">
      <p className="dw-muted">
        Favicon — the icon in browser tabs. Upload an image (SVG/PNG/ICO); it sets
        the <code>icon:</code> key above. <strong>Save + publish</strong> to apply.
      </p>
      <div className="dw-favicon-row">
        <img
          className="dw-favicon-preview"
          src={previewSrc}
          alt="favicon preview"
          width={32}
          height={32}
        />
        <div className="dw-favicon-meta">
          <code>{icon || "— bundled default —"}</code>
          <div className="dw-favicon-actions">
            <button
              type="button"
              className="primary"
              disabled={!editable || busy}
              onClick={() => fileRef.current?.click()}
            >
              {busy ? "Uploading…" : icon ? "Replace favicon" : "Upload favicon"}
            </button>
            {icon && (
              <button
                type="button"
                disabled={!editable || busy}
                onClick={() => {
                  setText(writeIcon(text, ""));
                  setBust(Date.now());
                }}
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/svg+xml,image/png,image/x-icon,image/*,.ico,.svg"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onPick(f);
          e.target.value = ""; // allow re-picking the same file
        }}
      />
      {err && <pre className="dw-error">{err}</pre>}
    </div>
  );
}
